<script setup lang="ts">
import { computed } from '#imports';
import { UiCheckbox } from '../checkbox';
import { UiListItemType } from './ListItemType';
import { UiIconName, UiIconTemplate } from '../icon';
import type { QaID } from '../../utils/types';

const props = withDefaults(defineProps<{
  selected?: boolean;
  disabled?: boolean;
  type?: UiListItemType;
  indent?: number;
  locale?: string;
  qa?: QaID;
}>(), {
  selected: false,
  disabled: false,
  type: UiListItemType.Default,
  indent: 0,
  locale: '',
  qa: '',
});

const classView = computed(() => {
  const result = [];
  
  if (props.selected && props.type !== UiListItemType.Checkbox) {
    result.push('_selected');
  }
  
  if (props.disabled) {
    result.push('_disabled');
  }
  
  return result;
});

defineEmits(['click', 'mouseenter']);
</script>

<template lang="pug">
li.ui-list-item(
  :class="classView"
  :data-qa="qa + locale"
  @click.stop="disabled ? null : $emit('click')"
  @mouseenter="$emit('mouseenter')"
)
  template(v-if="type === UiListItemType.Checkbox")
    UiCheckbox(:locale="locale" :style="{'margin-left': `${indent}px`}" :modelValue="selected")
    slot
  template(v-else)
    slot(name="leftIcon")
    slot
      | {{ $t(locale) }}
    slot(name="rightIcon")
      .selected-icon
        UiIconTemplate(
          v-if="selected"
          :name="UiIconName.UserInterface_DoneThin"
          :size="20"
        )
</template>

<style lang="scss" scoped>
@import '../../styles/colors';
@import '../../styles/typography';

.ui-list-item {
  @include ui-typography-M-1;
  font-family: $body-font-family;
  display: flex;
  padding: 8px 12px;
  margin: 0;
  align-items: center;

  &._selected {
    background: $ui-color-selector-bg-selected;
    color: $ui-color-text-white;

    &:hover {
      background: $ui-color-selector-bg-selected;
    }
  }

  &._disabled {
    background: $ui-color-input-bg-disabled;
  }

  &:hover {
    background: $ui-color-selector-bg-hover;
    cursor: pointer;
  }

  .selected-icon {
    display: flex;
    justify-content: flex-end;
    width: 32px;
    height: 20px;
    margin-left: auto;
    color: $ui-color-text-white;
  }
}
</style>
